import React, { useEffect, useState, useRef } from "react";
import { Provider } from "react-redux";
import { ProductDetailPageResult } from "../ProductDetailPageResult";
import { Product } from "~/common/Store/models/ProductListing";
import Store from "~/common/Store";
import ProductBox from "~/productListing/ProductBox";
import { User } from "~/common/Store/models/User";
import fetchSimilarItems from "./fetchSimilarItems";
import "./product-box-add-to-cart.less";

interface SimilarProductsContainerProps {
  product: ProductDetailPageResult;
  mounted: boolean;
  user: User;
}

interface AutoReorderItemsRequest {
  itemNumbers: string[];
}

const extractItemNumbers = (similarProducts: Product[]): string[] => {
  const itemNumbers: string[] = [];
  similarProducts?.forEach((product) => {
    if (!product.grouping) {
      itemNumbers.push(product.itemNumber.toUpperCase());
    } else {
      product.groupingAttributes?.groupMembers.forEach((groupMember) =>
        itemNumbers.push(groupMember.itemNumber.toUpperCase()),
      );
    }
  });
  return itemNumbers;
};

const populateItemsOnReorder = async (
  similarProducts: Product[],
  setItemsOnAutoReorder: React.Dispatch<
    React.SetStateAction<readonly string[]>
  >,
) => {
  const endpointUrl = "/api/myaccount/autoreorderitems";
  const request: AutoReorderItemsRequest = {
    itemNumbers: extractItemNumbers(similarProducts),
  };

  try {
    const response = await fetch(endpointUrl, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(
        `Failed to fetch auto reorder items, status ${response.status}`,
      );
    }

    const result = await response.json<string[]>();

    setItemsOnAutoReorder(result.map((i: string) => i.toLowerCase()));
  } catch {
    // Do nothing
  }
};

const Container = ({
  product,
  mounted,
  user,
}: SimilarProductsContainerProps) => {
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const isSimilarProductsComponentMounted = useRef(false);
  const [itemsOnAutoReorder, setItemsOnAutoReorder] = useState<string[]>([]);
  const [isQuantityDiscountPopoverOpen, setIsQuantityDiscountPopoverOpen] =
    useState("");

  const getSimilarItems = async (activeTemplate: ProductDetailPageResult) => {
    const similarItems = await fetchSimilarItems(
      activeTemplate.groupingAttributes.isGroupMaster
        ? activeTemplate.originalId
        : activeTemplate.itemNumber,
      activeTemplate.title,
      [],
    );

    if (!similarItems.isError && isSimilarProductsComponentMounted.current) {
      setSimilarProducts(similarItems.value);
    }
  };

  useEffect(() => {
    isSimilarProductsComponentMounted.current = true;

    if (mounted) {
      getSimilarItems(product);
    }

    return () => {
      isSimilarProductsComponentMounted.current = false;
    };
  }, [mounted, product]);

  useEffect(() => {
    if (user.registered) {
      populateItemsOnReorder([...similarProducts], setItemsOnAutoReorder);
    }
  }, [similarProducts, user.registered]);

  return (
    <>
      {similarProducts?.length > 0 && (
        <section data-testid="similar-products" className="-mx-2.5 xs:mx-0">
          <h2 className="ml-4 xs:ml-0">Similar Products</h2>
          <Provider store={Store}>
            <div
              id="product_listing"
              data-testid="product-listing-container"
              className="ag ag-grid overflow-hidden
              grid grid-cols-2 xsl:grid-cols-3 lz:grid-cols-4
              xxl:grid-cols-5 xxxl:grid-cols-6 border border-r-0 border-b-0 border-gray-200 border-solid xs:rounded"
            >
              {similarProducts.map((product, index) => (
                <ProductBox
                  key={product.itemNumber}
                  product={product}
                  mounted={mounted}
                  user={user}
                  lazyLoadImages={index > 12}
                  index={index}
                  itemsOnAutoReorder={itemsOnAutoReorder}
                  setIsQuantityDiscountPopoverOpen={
                    setIsQuantityDiscountPopoverOpen
                  }
                  isQuantityDiscountPopoverOpen={isQuantityDiscountPopoverOpen}
                  productListingView="grid"
                />
              ))}
            </div>
          </Provider>
        </section>
      )}
    </>
  );
};

export default Container;
